/**
 * Сторис — промо на главной приложения: кружок с подписью, внутри слайды 9:16.
 * Здесь — то, что экраны считают сами: статус показа, период, порядок, новый
 * слайд. Правила полей — в `@dorixona/api/stories` (контракт для бэкенда).
 */
import type { CSSProperties } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import {
  STORY_DURATION_DEFAULT,
  STORY_MEDIA_MAX_BYTES,
  STORY_SLIDES_MAX,
} from '@dorixona/api/stories'
import { addDaysMs, formatDate, isoDay, isoString, parseIsoDay, parseMs } from '@dorixona/utils'
import { isApiError } from '@dorixona/api'
import { failureText } from '../../ui/Load'
import type { Story, StorySlide, StoryToSave } from './api'

export type StoryStatus = 'live' | 'scheduled' | 'ended' | 'off' | 'empty'

/** Видна ли сторис покупателю сейчас, а если нет — почему. */
export function storyStatus(story: Story, now: number): StoryStatus {
  if (!story.active) return 'off'
  if (story.slides.length === 0) return 'empty'
  if (story.startsAt !== null && parseMs(story.startsAt) > now) return 'scheduled'
  if (story.endsAt !== null && parseMs(story.endsAt) <= now) return 'ended'
  return 'live'
}

export const STATUS_TEXT: Record<StoryStatus, string> = {
  live: 'в приложении',
  scheduled: 'запланирована',
  ended: 'показ закончился',
  off: 'выключена',
  empty: 'нет слайдов',
}

/** Тон — «чья очередь»: зелёный — работает, жёлтый — нужно действие. */
export const STATUS_TONE: Record<StoryStatus, string | undefined> = {
  live: 'success',
  scheduled: 'info',
  ended: undefined,
  off: undefined,
  empty: 'warning',
}

/**
 * Период показа по дням: «с» — с полуночи, «по» — включительно, до полуночи
 * следующего дня. Так человек выбирает даты, а сервер получает точные метки.
 */
export function periodToInput(story: Pick<Story, 'startsAt' | 'endsAt'>): {
  from: string
  to: string
} {
  return {
    from: story.startsAt ? isoDay(parseMs(story.startsAt)) : '',
    to: story.endsAt ? isoDay(addDaysMs(parseMs(story.endsAt), -1)) : '',
  }
}

export function periodFromInput(
  from: string,
  to: string,
): { startsAt: string | null; endsAt: string | null } {
  const start = parseIsoDay(from)
  const end = parseIsoDay(to)
  return {
    startsAt: start === null ? null : isoString(start),
    endsAt: end === null ? null : isoString(addDaysMs(end, 1)),
  }
}

/** «12.10.2026 — 20.10.2026», «с 12.10.2026», «до 20.10.2026», «всегда». */
export function periodText(story: Pick<Story, 'startsAt' | 'endsAt'>): string {
  const { from, to } = periodToInput(story)
  const day = (value: string) => formatDate(parseIsoDay(value) as number)
  if (from && to) return `${day(from)} — ${day(to)}`
  if (from) return `с ${day(from)}`
  if (to) return `до ${day(to)}`
  return 'без срока'
}

/** Конец показа раньше начала — сохранять нечего. */
export function periodError(from: string, to: string): string {
  const start = parseIsoDay(from)
  const end = parseIsoDay(to)
  return start !== null && end !== null && end < start ? 'Конец показа раньше начала' : ''
}

export const sortedStories = (stories: Story[]): Story[] =>
  [...stories].sort((a, b) => a.sort - b.sort || a.id - b.id)

/** Как у категорий: прежние значения порядка раздаются по новому списку. */
export function reorderStories(stories: Story[], activeId: number, overId: number): Story[] {
  const all = sortedStories(stories)
  const from = all.findIndex((s) => s.id === activeId)
  const to = all.findIndex((s) => s.id === overId)
  if (from < 0 || to < 0) return all
  const sorts = all.map((s) => s.sort)
  const distinct = new Set(sorts).size === sorts.length
  return arrayMove(all, from, to).map((s, i) => ({
    ...s,
    sort: distinct ? (sorts[i] as number) : i + 1,
  }))
}

export function changedOrder(before: Story[], after: Story[]): Story[] {
  return after.filter((s) => before.find((b) => b.id === s.id)?.sort !== s.sort)
}

/**
 * Новая сторис — одна подпись. Слайды, обложку и период добавляют в
 * карточке; встаёт последней и выключена: пустой кружок покупателю ни к чему.
 */
export function newStory(titleRu: string, stories: Story[]): StoryToSave {
  return {
    id: 0,
    titleRu: titleRu.trim(),
    titleUz: '',
    coverKey: '',
    coverUrl: '',
    slides: [],
    startsAt: null,
    endsAt: null,
    sort: stories.reduce((max, s) => Math.max(max, s.sort), 0) + 1,
    active: false,
  }
}

export function newSlide(media: { key: string; url: string }): StorySlide {
  return {
    key: media.key,
    url: media.url,
    duration: STORY_DURATION_DEFAULT,
    buttonRu: '',
    buttonUz: '',
    link: '',
  }
}

/** У слайда есть кнопка: подпись хотя бы на одном языке. */
export const hasButton = (slide: Pick<StorySlide, 'buttonRu' | 'buttonUz'>): boolean =>
  slide.buttonRu.trim() !== '' || slide.buttonUz.trim() !== ''

/**
 * Куда может вести кнопка — то же правило, что у сервера и у приложения: путь
 * приложения с одного `/` (`//host` — чужой сайт без схемы) или `https://…`.
 */
export function isAppLink(link: string): boolean {
  return (
    (link.startsWith('/') && !link.startsWith('//') && !link.startsWith('/\\')) ||
    (link.startsWith('https://') && link.length > 'https://'.length)
  )
}

/** Что не так со слайдом — пусто, если всё в порядке. Те же правила, что у сервера. */
export function slideError(slide: StorySlide): string {
  if (!hasButton(slide)) return ''
  const link = slide.link.trim()
  if (!link) return 'Выберите, куда ведёт кнопка'
  if (!isAppLink(link)) return 'Адрес — путь в приложении (/…) или сайт https://…'
  return ''
}

/** Ссылка уходит только с кнопкой: без подписи её не видно и не нажать. */
export const linkToSave = (slide: StorySlide): string => (hasButton(slide) ? slide.link.trim() : '')

/** Куда ведёт кнопка — разделы приложения, а не пути, которые надо знать. */
export type LinkKind = '' | 'catalog' | 'category' | 'product' | 'news' | 'stores' | 'custom'

export interface LinkChoice {
  kind: LinkKind
  /** Номер категории или товара; 0 — ещё не выбран. */
  id: number
  /** Свой адрес — для `custom`. */
  custom: string
}

export const LINK_KINDS: { value: LinkKind; label: string }[] = [
  { value: '', label: 'Не выбрано' },
  { value: 'catalog', label: 'Каталог' },
  { value: 'category', label: 'Категория' },
  { value: 'product', label: 'Товар' },
  { value: 'news', label: 'Новости' },
  { value: 'stores', label: 'Аптеки на карте' },
  { value: 'custom', label: 'Свой адрес' },
]

/** Сохранённая ссылка → раздел приложения; незнакомая — «свой адрес». */
export function parseLink(link: string): LinkChoice {
  const value = link.trim()
  const none = { id: 0, custom: '' }
  if (value === '') return { kind: '', ...none }
  if (value === '/catalog') return { kind: 'catalog', ...none }
  if (value === '/news') return { kind: 'news', ...none }
  if (value === '/stores') return { kind: 'stores', ...none }
  const category = /^\/catalog\?cat=(\d+)$/.exec(value)
  if (category) return { kind: 'category', id: Number(category[1]), custom: '' }
  const product = /^\/catalog\/(\d+)$/.exec(value)
  if (product) return { kind: 'product', id: Number(product[1]), custom: '' }
  return { kind: 'custom', id: 0, custom: value }
}

/** Раздел → путь приложения; категория и товар без выбора — пусто. */
export function buildLink(choice: LinkChoice): string {
  switch (choice.kind) {
    case 'catalog':
      return '/catalog'
    case 'news':
      return '/news'
    case 'stores':
      return '/stores'
    case 'category':
      return choice.id ? `/catalog?cat=${choice.id}` : ''
    case 'product':
      return choice.id ? `/catalog/${choice.id}` : ''
    case 'custom':
      return choice.custom
    default:
      return ''
  }
}

/** Сколько секунд идёт вся сторис — подсказка у списка слайдов. */
export const totalSeconds = (slides: StorySlide[]): number =>
  slides.reduce((sum, slide) => sum + slide.duration, 0)

export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp'

/** Файлы, которые не возьмём, — и почему; остальные уйдут в загрузку. */
export function pickFiles(files: File[], room: number): { ok: File[]; error: string } {
  const images = files.filter((f) => IMAGE_ACCEPT.split(',').includes(f.type))
  if (images.length < files.length) return { ok: [], error: 'Нужны картинки JPEG, PNG или WebP' }
  if (images.some((f) => f.size > STORY_MEDIA_MAX_BYTES)) {
    return { ok: [], error: 'Файл больше 5 МБ' }
  }
  if (images.length > room) {
    return {
      ok: images.slice(0, room),
      error: `В сторис не больше ${STORY_SLIDES_MAX} слайдов — лишние файлы не добавлены`,
    }
  }
  return { ok: images, error: '' }
}

/**
 * Почему не сохранилось. Отказ проверки называет причину («Без слайдов сторис
 * не показать») — её и показываем; остальное — как везде в админке.
 */
export function storyFailureText(error: unknown): string {
  if (
    isApiError(error) &&
    (error.code === 'validation_failed' || error.code === 'payload_too_large') &&
    error.message !== error.code
  ) {
    return error.message
  }
  return failureText(error)
}

/** Сдвиг перетаскиваемой строки или плитки — только по своей оси, без наклона. */
export function dragStyle(
  transform: { x: number; y: number } | null,
  transition: string | undefined,
  axis: 'x' | 'y',
): CSSProperties {
  const shift = transform ? Math.round(axis === 'x' ? transform.x : transform.y) : 0
  return {
    transform: transform
      ? axis === 'x'
        ? `translate3d(${shift}px, 0, 0)`
        : `translate3d(0, ${shift}px, 0)`
      : undefined,
    transition,
  }
}
